import { styled, useTheme } from '@mui/material/styles';
import { Box, Tooltip } from '@mui/material';
import { useToggleTheme } from 'src/theme/ThemeProvider';

const ThemeToggleWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '4px',
  marginRight: theme.spacing(1),
  transition: 'transform 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
  '&:active': {
    transform: 'scale(0.95)'
  }
}));

const ToggleTrack = styled(Box)(({ checked }) => ({
  width: 60,
  height: 30,
  borderRadius: 30,
  padding: 4,
  background: checked
    ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)' // Night gradient
    : 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)', // Day gradient
  boxShadow: checked
    ? 'inset 0 2px 5px rgba(0,0,0,0.4), 0 1px 1px rgba(255,255,255,0.05)'
    : 'inset 0 2px 5px rgba(0,0,0,0.1), 0 1px 1px rgba(255,255,255,0.4)',
  transition: 'background 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden'
}));

const ToggleKnob = styled(Box)(({ theme, checked }) => ({
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: checked ? '#fff' : '#fff', // Moon color vs Sun color base
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  position: 'absolute',
  top: 4,
  left: checked ? 34 : 4,
  transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  '&::before': {
    // Sun rays or Moon crater effect
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: checked
      ? 'radial-gradient(circle at 70% 30%, transparent 0%, transparent 15%, #e0e0e0 16%, #e0e0e0 20%, transparent 21%)' // Moon craters
      : 'radial-gradient(circle, #ffd54f 40%, transparent 70%)', // Sun glow over white
    opacity: checked ? 1 : 0.8
  }
}));

const Star = styled(Box)(({ top, left, size, delay }) => ({
  position: 'absolute',
  top,
  left,
  width: size,
  height: size,
  borderRadius: '50%',
  background: '#fff',
  opacity: 0,
  animation: 'twinkle 2s infinite',
  animationDelay: delay,
  '@keyframes twinkle': {
    '0%, 100%': { opacity: 0.2, transform: 'scale(0.8)' },
    '50%': { opacity: 1, transform: 'scale(1.2)' }
  }
}));

const Cloud = styled(Box)(({ top, left, delay }) => ({
  position: 'absolute',
  top,
  left,
  width: 20,
  height: 8,
  borderRadius: 4,
  background: 'rgba(255,255,255,0.8)',
  animation: 'float 3s infinite ease-in-out',
  animationDelay: delay,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    background: 'inherit',
    borderRadius: '50%'
  },
  '&::before': { width: 10, height: 10, top: -5, left: 3 },
  '&::after': { width: 8, height: 8, top: -3, left: 10 },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateX(0)' },
    '50%': { transform: 'translateX(3px)' }
  }
}));

function ThemeToggle() {
  const { toggleTheme } = useToggleTheme();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Tooltip
      title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      arrow
    >
      <ThemeToggleWrapper onClick={toggleTheme}>
        <ToggleTrack checked={isDark}>
          {isDark ? (
            <>
              <Star top="20%" left="20%" size={2} delay="0s" />
              <Star top="50%" left="40%" size={1.5} delay="0.5s" />
              <Star top="30%" left="60%" size={2} delay="1s" />
              <Star top="70%" left="30%" size={1} delay="1.5s" />
            </>
          ) : (
            <>
              <Cloud top="20%" left="50%" delay="0s" />
              <Cloud top="50%" left="20%" delay="1s" />
            </>
          )}
          <ToggleKnob checked={isDark}>
            {!isDark && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, #fdd835 0%, #fbc02d 100%)',
                  boxShadow: '0 0 10px #fbc02d'
                }}
              />
            )}
            {isDark && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: '#e0e0e0',
                  boxShadow: 'inset -3px -3px 5px rgba(0,0,0,0.1)'
                }}
              />
            )}
          </ToggleKnob>
        </ToggleTrack>
      </ThemeToggleWrapper>
    </Tooltip>
  );
}

export default ThemeToggle;
